// Domain types shared between the React frontend and the Pages Functions API.

export type Role = "btzl" | "client";

export interface SessionUser {
  id: number;
  email: string;
  role: Role;
  clientId: number | null;
  clientName: string | null;
}

export type ProjectStatus =
  | "In entitlement"
  | "Pre-construction"
  | "Under construction"
  | string;

export interface Client {
  id: number;
  name: string;
}

// Admin view of a user (BTZL-only user management). No secrets ever shipped.
export interface AdminUser {
  id: number;
  email: string;
  role: Role;
  clientId: number | null;
  clientName: string | null;
  createdAt: string;
}

// Shared password policy (enforced server-side; surfaced in the UI).
export const MIN_PASSWORD_LENGTH = 8;

export interface Project {
  id: number;
  clientId: number;
  name: string;
  location: string | null;
  type: string | null;
  units: number | null;
  status: ProjectStatus;
  estimate: number | null;
  startDate: string | null;
  endDate: string | null;
  hasDetail: boolean; // true when transaction-level cash flow / ledger exists
}

export type SovConfidence =
  | "Confirmed"
  | "Pending Confirmation"
  | "Flagged"
  | "Missing"
  | "Estimate"
  | string;

export interface SovLine {
  id: number;
  projectId: number;
  category: string;
  estimate: number | null;
  budget: number | null;
  actualToDate: number | null;
  percentComplete: number | null;
  confidence: SovConfidence;
}

// Capital structure (Debt vs Equity) — ingested from the `Capital Structure`
// sheet. Each financing-movement category is classified debt or equity.
export type CapitalKind = "debt" | "equity";

export interface CapitalStructureRow {
  id: number;
  category: string; // e.g. "Bank Draw", "Investor Contribution"
  amount: number;
  kind: CapitalKind;
}

export interface CapitalStructureResponse {
  rows: CapitalStructureRow[];
  debtTotal: number;
  equityTotal: number;
  total: number;
}

export type CashFlowLine =
  | "Sale"
  | "Bank Draws"
  | "Investor Contribution"
  | "Material"
  | "Labor"
  | "Other"
  | "Debt Service"
  | string;

export interface CashFlowCell {
  id: number;
  projectId: number;
  weekLabel: string;
  weekIndex: number;
  lineType: CashFlowLine;
  amount: number;
}

export interface ProjectMetric {
  id: number;
  projectId: number;
  label: string;
  value: number | null;
  rawValue: string | null;
}

// Native ledger statuses (kept verbatim from the Excel LEDGER_OUT).
export type MatchStatus =
  | "Reconciled"
  | "Variance"
  | "Missing Support"
  | "Vendor match — amount differs (review)"
  | "Financing — no receipt"
  | string;

// Canonical SOV categories the client picks from when assigning a movement.
export const SOV_CATEGORIES = [
  "Materials",
  "Subcontractor",
  "Labor",
  "Permits and Fees",
  "Equipment",
  "Overhead",
  "Other",
] as const;

export interface ReconciliationItem {
  id: number;
  projectId: number | null; // null = "Needs Review — assign project"
  projectName: string | null; // resolved for display
  sourceProjectId: number | null; // original project from Excel
  rowRef: string;
  date: string | null;
  weekLabel: string | null;
  sourceType: string | null; // Expense | Financing
  movementType: string;
  cashDirection: string | null; // Inflow | Outflow
  counterparty: string | null;
  amount: number;
  cashImpact: number;
  matchStatus: MatchStatus;
  sourceMatchStatus: MatchStatus | null; // original status from Excel
  receiptId: string | null;
  sourceRef: string | null;
  items: string | null;
  reviewFlags: string | null;
  sovStage: string | null; // editable: assigned category
  sourceSovStage: string | null; // original SOV stage from Excel
  note: string | null;
  assignedBy: number | null;
  assignedAt: string | null;
  updatedBy: number | null;
  updatedByEmail: string | null; // resolved for the BTZL "edited by" column
  updatedAt: string | null;
}

// Submitted / orphan receipts (supporting evidence for reconciliation).
export interface Receipt {
  id: number;
  receiptId: string;
  date: string | null;
  weekLabel: string | null;
  vendorClean: string | null;
  projectRaw: string | null;
  sovStage: string | null;
  total: number | null;
  paymentMethod: string | null;
  sourceRef: string | null;
  isOrphan: boolean;
  projectId: number | null;
}

export type FlagSeverity = "info" | "warning" | "critical";
export type FlagSource = "auto" | "manual";
export type FlagStatus = "open" | "closed";

export interface Flag {
  id: number | string; // auto flags use "auto:<key>"; manual flags are numeric
  projectId: number;
  severity: FlagSeverity;
  type: string;
  message: string;
  source: FlagSource;
  status: FlagStatus;
  createdBy: number | null;
  createdAt: string;
}

export type TaskStatus = "todo" | "in_progress" | "review" | "done";
export type TaskRecurrence = "once" | "weekly" | "monthly";

export interface Task {
  id: number;
  projectId: number | null;
  clientId: number;
  assigneeLabel: string | null;
  title: string;
  description: string | null;
  workflowStage: string | null;
  cycle: string | null;
  channel: string | null;
  sequence: number | null;
  receivesFrom: string | null; // raw "Receives from" cell (encodes dependency task codes)
  sourceRef: string | null; // workflow task code (e.g. "1.1.3"), used as dependency node id
  status: TaskStatus;
  recurrence: TaskRecurrence;
  recurrenceParentId: number | null;
  dueDate: string | null;
  createdBy: number | null;
  updatedAt: string | null;
}

// ---- Page access (admin chooses which views the client sees) --------------
export type PageKey = "summary" | "cash-flow" | "reconciliation" | "tasks" | "activity";
export const PAGE_KEYS: PageKey[] = ["summary", "cash-flow", "reconciliation", "tasks", "activity"];
export const PAGE_LABELS: Record<PageKey, string> = {
  summary: "Executive Summary",
  "cash-flow": "Cash Flow",
  reconciliation: "Reconciliation",
  tasks: "Tasks & Workflow",
  activity: "Activity",
};

export interface MeResponse {
  user: SessionUser;
  visiblePages: PageKey[]; // BTZL = all
}

export interface ClientPageAccessResponse {
  clientId: number;
  pages: Record<PageKey, boolean>;
}

export interface ApiError {
  error: string;
}

// ---- Composite API responses ----------------------------------------------

export interface ProjectBudgetRow {
  id: number;
  name: string;
  status: ProjectStatus;
  budget: number;
  spent: number;
  utilizationPct: number; // ratio 0..1+
  estimate: number | null;
  activeFlags: number;
  topSeverity: FlagSeverity | null;
  hasDetail: boolean;
}

export interface PortfolioSummary {
  kpis: {
    netCashFlow: number;
    netCashFlowProjects: number; // # projects contributing (with detail)
    budgetTotal: number;
    budgetSpent: number;
    budgetUtilizationPct: number;
    projectCount: number;
    projectsByStatus: { status: string; count: number }[];
  };
  projects: ProjectBudgetRow[];
}

export interface ProjectDetail {
  project: Project;
  sovLines: SovLine[];
  metrics: ProjectMetric[];
}

// Cash-flow movement class (derived from Movement_Type / cash_flows.line_type).
export type CashFlowClass = "operating" | "financing" | "investing";

export interface CashFlowClassTotal {
  cls: CashFlowClass;
  inflow: number;
  outflow: number;
  net: number;
}

export interface CashFlowWeek {
  weekIndex: number;
  weekLabel: string;
  inflow: number;
  outflow: number;
  net: number;
  cumulative: number;
  operatingNet: number;
  financingNet: number;
}

export interface CashFlowResponse {
  hasDetail: boolean;
  scope: "project" | "portfolio";
  series: CashFlowWeek[];
  byClass: CashFlowClassTotal[]; // Operating / Financing / Investing net + inflow/outflow
  ledger: ReconciliationItem[]; // transaction-level detail (empty for the portfolio view)
}

// Spend by SOV category, sourced from the canonical Budget SOV (sov_lines).
export interface SovCategorySpend {
  category: string;
  budget: number;
  actual: number;
}

export interface SovSpendResponse {
  byCategory: SovCategorySpend[]; // all projects combined
  byProject: {
    projectId: number;
    projectName: string;
    budget: number;
    actual: number;
    lines: SovCategorySpend[];
  }[];
}

export interface ReconciliationResponse {
  items: ReconciliationItem[];
  byStatus: { matchStatus: string; count: number; amount: number }[];
  byCategory: {
    category: string;
    reconciled: number;
    unreconciled: number;
    total: number;
    count: number;
  }[];
  totals: { reconciled: number; unreconciled: number; total: number };
  categories: string[]; // canonical SOV categories for the assign dropdown
  needsReview: number; // movements with no project/category or non-reconciled status
  canEdit: boolean;
}

// ---- Reconciliation export (admin: current state + diff vs original) -------
export interface ReconExportRow {
  id: number; // reconciliation_items.id (used by the Activity revert action)
  rowRef: string;
  date: string | null;
  weekLabel: string | null;
  counterparty: string | null;
  movementType: string;
  amount: number;
  cashImpact: number;
  original: { project: string | null; sovStage: string | null; matchStatus: string | null };
  current: { project: string | null; sovStage: string | null; matchStatus: string | null };
  changed: boolean;
  editedByEmail: string | null;
  editedAt: string | null;
}

export interface ReconExportResponse {
  rows: ReconExportRow[];
  generatedAt: string;
}
